<template>
  <div class="time-picker" :class="{ disabled: disabled }">
    <input
      type="text"
      class="time-picker-input"
      :value="displayValue"
      :disabled="disabled"
      readonly
      @click="openPicker"
    />
    <div v-if="isOpen && !disabled" class="time-picker-dropdown">
      <div class="time-picker-controls">
        <div class="time-column">
          <button type="button" class="time-btn up" @click="incrementHour">
            <span class="arrow-up"></span>
          </button>
          <input
            type="text"
            class="time-value"
            :value="paddedHour"
            readonly
          />
          <button type="button" class="time-btn down" @click="decrementHour">
            <span class="arrow-down"></span>
          </button>
        </div>
        <div class="time-separator">:</div>
        <div class="time-column">
          <button type="button" class="time-btn up" @click="incrementMinute">
            <span class="arrow-up"></span>
          </button>
          <input
            type="text"
            class="time-value"
            :value="paddedMinute"
            readonly
          />
          <button type="button" class="time-btn down" @click="decrementMinute">
            <span class="arrow-down"></span>
          </button>
        </div>
      </div>
      <button type="button" class="mt-4 time-picker-done" @click="closePicker">OK</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TimePicker',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    minHour: {
      type: Number,
      default: 0
    },
    maxHour: {
      type: Number,
      default: 23
    },
    stepMinutes: {
      type: Number,
      default: 15
    },
    defaultHour: {
      type: Number,
      default: 11
    },
    defaultMinute: {
      type: Number,
      default: 0
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false,
      hour: this.defaultHour,
      minute: this.defaultMinute
    };
  },
  computed: {
    paddedHour() {
      return String(this.hour).padStart(2, '0');
    },
    paddedMinute() {
      return String(this.minute).padStart(2, '0');
    },
    displayValue() {
      if (!this.modelValue) {
        return '';
      }
      return this.modelValue;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const parts = newVal.split(':');
          if (parts.length >= 2) {
            const parsedHour = parseInt(parts[0], 10);
            const parsedMinute = parseInt(parts[1], 10);
            this.hour = isNaN(parsedHour) ? this.defaultHour : parsedHour;
            this.minute = isNaN(parsedMinute) ? this.defaultMinute : parsedMinute;
          }
        } else {
          this.hour = this.defaultHour;
          this.minute = this.defaultMinute;
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    openPicker() {
      if (!this.disabled) {
        this.isOpen = true;
      }
    },
    closePicker() {
      this.isOpen = false;
      this.emitValue();
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        if (this.isOpen) {
          this.closePicker();
        }
      }
    },
    incrementHour() {
      this.hour = this.hour >= this.maxHour ? this.minHour : this.hour + 1;
      this.emitValue();
    },
    decrementHour() {
      this.hour = this.hour <= this.minHour ? this.maxHour : this.hour - 1;
      this.emitValue();
    },
    incrementMinute() {
      this.minute += this.stepMinutes;
      if (this.minute >= 60) {
        this.minute = 0;
        this.incrementHour();
      } else {
        this.emitValue();
      }
    },
    decrementMinute() {
      this.minute -= this.stepMinutes;
      if (this.minute < 0) {
        this.minute = 60 - this.stepMinutes;
        this.decrementHour();
      } else {
        this.emitValue();
      }
    },
    emitValue() {
      const value = `${this.paddedHour}:${this.paddedMinute}`;
      this.$emit('update:modelValue', value);
    }
  }
};
</script>

<style scoped>
.time-picker {
  position: relative;
  display: inline-block;
}

.time-picker.disabled {
  opacity: 0.6;
}

.time-picker-input {
  width: 80px;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  background-color: var(--bo-surface);
}

.time-picker.disabled .time-picker-input {
  cursor: not-allowed;
  background-color: var(--bo-line);
}

.time-picker-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: var(--bo-surface);
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px;
  margin-top: 2px;
}

.time-picker-controls {
  display: flex;
  align-items: center;
  gap: 5px;
}

.time-column {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.time-btn {
  width: 36px;
  height: 28px;
  border: 1px solid #ced4da;
  background: var(--bo-surface-2);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.time-btn:hover {
  background: var(--bo-line);
}

.time-btn:active {
  background: var(--bo-line);
}

.arrow-up {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 6px solid #495057;
}

.arrow-down {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #495057;
}

.time-value {
  width: 36px;
  height: 32px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  border: 1px solid #ced4da;
  border-radius: 4px;
  margin: 4px 0;
  background: var(--bo-surface);
}

.time-separator {
  font-size: 20px;
  font-weight: bold;
  color: var(--bo-ink-2);
  padding: 0 2px;
}

.time-picker-done {
  width: 100%;
  margin-top: 10px;
  padding: 6px 12px;
  background: #ECB109;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.time-picker-done:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>
